import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Bell, Check, X, Users } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

interface LinkRequest {
  id: string;
  parent_id: string;
  parent_email: string;
  child_username: string;
  requested_at: string;
  status: string;
}

export default function LinkRequestNotifications() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<LinkRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    // Request notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    // Initial fetch of pending requests
    const loadRequests = async () => {
      try {
        const { data, error } = await supabase
          .from("parent_child_link_requests")
          .select("*")
          .eq("child_id", user.id)
          .eq("status", "pending")
          .order("requested_at", { ascending: false });

        if (error) {
          console.error("Failed to load link requests:", error);
        } else {
          setRequests(data || []);
        }
      } catch (error) {
        console.error("Failed to load link requests:", error);
      } finally {
        setLoading(false);
      }
    };

    loadRequests();

    // Subscribe to real-time changes for new requests
    const channel = supabase
      .channel("link-requests-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "parent_child_link_requests",
          filter: `child_id=eq.${user.id}`,
        },
        (payload) => {
          console.log("New link request received:", payload);
          const newRequest = payload.new as LinkRequest;
          if (newRequest.status === "pending") {
            setRequests((prev) => [newRequest, ...prev]);
            // Optional: Show browser notification
            if (
              "Notification" in window &&
              Notification.permission === "granted"
            ) {
              new Notification("New Parent Link Request", {
                body: `${newRequest.parent_email} wants to view your progress`,
                icon: "/favicon.png",
              });
            }
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "parent_child_link_requests",
          filter: `child_id=eq.${user.id}`,
        },
        (payload) => {
          console.log("Link request updated:", payload);
          const updatedRequest = payload.new as LinkRequest;
          // Remove from list if no longer pending
          if (updatedRequest.status !== "pending") {
            setRequests((prev) =>
              prev.filter((r) => r.id !== updatedRequest.id),
            );
          }
        },
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const respondToRequest = async (
    requestId: string,
    action: "approve" | "deny",
  ) => {
    setResponding(requestId);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error("Not authenticated");

      const SUPABASE_URL =
        import.meta.env.VITE_SUPABASE_URL || "http://localhost:54321";
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/child-respond-link-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ request_id: requestId, action }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to respond to request");
      }

      // Remove the request from the list
      setRequests((prev) => prev.filter((r) => r.id !== requestId));

      // Show success message
      alert(data.message);
    } catch (error) {
      console.error("Failed to respond to request:", error);
      alert(
        error instanceof Error ? error.message : "Failed to respond to request",
      );
    } finally {
      setResponding(null);
    }
  };

  if (loading) return null;
  if (requests.length === 0) return null;

  return (
    <Card className="border-2 border-blue-200 bg-blue-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-blue-900">
          <Bell className="w-5 h-5" />
          Parent Link Requests ({requests.length})
        </CardTitle>
        <CardDescription className="text-blue-700">
          Parents want to view your progress. Approve only if you know them!
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {requests.map((request) => (
          <Alert key={request.id} className="bg-white">
            <Users className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <div className="flex-grow">
                <p className="font-semibold">{request.parent_email}</p>
                <p className="text-xs text-gray-500">
                  Requested{" "}
                  {new Date(request.requested_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => respondToRequest(request.id, "approve")}
                  disabled={responding === request.id}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => respondToRequest(request.id, "deny")}
                  disabled={responding === request.id}
                >
                  <X className="w-4 h-4 mr-1" />
                  Deny
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ))}
      </CardContent>
    </Card>
  );
}
